/**
 * ANY USED FUNCTION ALONG ONE OR MORE FILES THAT NO DEPENDS ON REACT NOR THE CONTEXT
 */
import {
	LocalStorageKey,
	NumFormat,
	DateFormat,
	TranslationKey,
	EnvKey,
	TableObject,
	Rect,
	Product,
	SafeZone,
	Journeys,
	Stage,
	Shortcut,
	SafeVenueData,
	Shift,
	ScoreMaxes,
} from '../types'
import {
	MONTH_NAMES_LONG,
	MONTH_NAMES_SHORT,
	DAY_NAMES_SHORT,
	ZONE_NAMES,
	DAY_NAMES_LONG,
	CHILDREN_PER_ADULT,
	TABLES_OVERRIDES,
	booking,
	ZONE_ORDERS,
	PRODUCTS_WICH_QTY_IS_FIXED_TO_PAX,
	cookies,
	SURCHARGE_NAME,
} from './symbols'
import { translator } from './symbols'

export const log = (...data: any) => {
	// if (!rumba.isProd()) console.log(...data)
}

export const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const removeDuplicates = (array: Array<number | string>) => Array.from(new Set(array))

export const load = <T>(from: 'localStorage' | 'sessionStorage', key: LocalStorageKey, defaultValue: T): T => {
    
    try { 

        const storedValue = from === 'sessionStorage' ? 
			sessionStorage.getItem(key) :
			localStorage.getItem(key)

        if (storedValue === null) return defaultValue
        
        const parsedValue = JSON.parse(storedValue);

		// if parsedValue & defaultValue are objects perform a safe load
		if (
			typeof defaultValue === 'object' &&
			!Array.isArray(defaultValue) &&
			defaultValue !== null &&
			typeof parsedValue === 'object' &&
			!Array.isArray(parsedValue) &&
			parsedValue !== null
		) {
			return {
				...defaultValue,
				...parsedValue,
			}
		}
		return parsedValue
	} catch (error) {
		return defaultValue
	}
}

export const deleteItem = (key: LocalStorageKey) => {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
}

export const loadEnvValue = (key: EnvKey): string => {
	const value = process.env[key]
	if (value === undefined) throw new Error(`${key} not found`)
	return value
}

export const save = (on: 'sessionStorage' | 'localStorage', key:LocalStorageKey, value: object | string | number) => on === 'sessionStorage' ? 
	sessionStorage.setItem(key, JSON.stringify(value)) :
	localStorage.setItem(key, JSON.stringify(value))

export const _ = (translationKey: TranslationKey, ...replace: Array<number | string>): string => {
	try {
		const translation = translator.getProcessedTranslation(translationKey, ...replace)
		if (!translation) throw new Error(`Unknown translation ${translationKey}`)
		return translation
	} catch (error) {
		log(error)
		return translationKey
	}
}

export const calcDiscount = (price: number, discount: number) => {
    if (!discount) return price
    return Math.round(price - (discount > -1 && discount < 1 ? price * discount : discount))
}

export const fixDecimals = (num: number, decimalsQty: number = 2) => Number(num.toFixed(decimalsQty))

export const parseNum = (format: NumFormat, num: number): string => {
	let parsedNum = ''

	switch (format) {
		case '2-digit':
			parsedNum = `${num < 0 ? '-' : ''}${num < 10 ? '0' : ''}${Math.floor(num)}`
			break
		case 'EUR':
			parsedNum = new Intl.NumberFormat('es-ES', {
				style: 'currency',
				currency: 'EUR',
				minimumFractionDigits: 0,
				maximumFractionDigits: 2,
			})
				.format(num)
				.replace(/\u00A0/g, '')
			break
		case 'percent':
			parsedNum = new Intl.NumberFormat('es-ES', {
				style: 'percent',
				minimumFractionDigits: 0,
				maximumFractionDigits: 2,
			}).format(num)
			break
		case 'es':
			parsedNum = new Intl.NumberFormat('es-ES', {
				minimumFractionDigits: 0,
				maximumFractionDigits: 2,
			}).format(num)
			break
		case 'discount':
			parsedNum = Math.abs(num) < 1 ? parseNum('percent', -1 * num) : parseNum('EUR', -1 * num)
			break
		default:
			parsedNum = String(num)
	}

	return parsedNum
}

export const getZoneName = (type: 'long' | 'short', zone: SafeZone, journey: Journeys) => {
	let result = ''
	switch (zone) {
		case 'za':
		case 'za2':
		case 'zb':
		case 'zc':
		case 'zv':
			if (type === 'long') result = `${_('0ge_zone___')} ${ZONE_NAMES[zone]}`
			else result = ZONE_NAMES[zone]
			break
		default:
			break
	}

	return result
}

export const parseDate = (format: DateFormat, date: string | number | Date, utc?: boolean): string => {
	try {
		const dateObj: Date = new Date(date)
		let parsedDate = ''
		if (isNaN(dateObj.getTime())) throw new Error(`Not possible to build a date object with ${date}`)

		const WEEK_DAY = utc ? dateObj.getUTCDay() : dateObj.getDay()
		const DATE = utc ? dateObj.getUTCDate() : dateObj.getDate()
		const MONTH = utc ? dateObj.getUTCMonth() : dateObj.getMonth()
		const YEAR = utc ? dateObj.getUTCFullYear() : dateObj.getFullYear()
		const HOURS = utc ? dateObj.getUTCHours() : dateObj.getHours()
		const MINUTES = utc ? dateObj.getUTCMinutes() : dateObj.getMinutes()
		const SECONDS = utc ? dateObj.getUTCSeconds() : dateObj.getSeconds()

		switch (format) {
			case 'Mon':
				parsedDate = DAY_NAMES_SHORT[WEEK_DAY]
				break
			case 'd':
				parsedDate = String(DATE)
				break
			case 'November yyyy':
				parsedDate = `${MONTH_NAMES_LONG[MONTH]} ${YEAR}`
				break
			case 'November':
				parsedDate = `${MONTH_NAMES_LONG[MONTH]}`
				break
			case 'Nov':
				parsedDate = MONTH_NAMES_SHORT[MONTH]
				break
			case 'd Nov yyyy':
				parsedDate = `${DATE} ${MONTH_NAMES_SHORT[MONTH]} ${YEAR}`
				break
			case 'Mon d November yyyy':
				parsedDate = `${DAY_NAMES_SHORT[WEEK_DAY]} ${DATE} ${MONTH_NAMES_LONG[MONTH]} ${YEAR}`
				break
			case 'Monday d November yyyy':
				parsedDate = `${DAY_NAMES_LONG[WEEK_DAY]} ${DATE} ${MONTH_NAMES_LONG[MONTH]} ${YEAR}`
				break
			case "d Nov 'yy":
				parsedDate = `${DATE} ${MONTH_NAMES_SHORT[MONTH]} '${String(YEAR).substring(2)}`
				break
			case 'yyyy-mm-dd':
				parsedDate = `${YEAR}-${String(MONTH + 1).padStart(2, '0')}-${String(DATE).padStart(2, '0')}`
				break
			case 'yyyy-mm':
				parsedDate = `${YEAR}-${String(MONTH + 1).padStart(2, '0')}`
				break
			case 'yyyy-mm-dd hh:mm:ss':
				parsedDate = `${YEAR}-${String(MONTH + 1).padStart(2, '0')}-${String(DATE).padStart(2, '0')} ${String(
					HOURS
				).padStart(2, '0')}:${String(MINUTES).padStart(2, '0')}:${String(SECONDS).padStart(2, '0')}`
				break
			case 'hh:mm':
				parsedDate = `${String(HOURS).padStart(2, '0')}:${String(MINUTES).padStart(2, '0')}`
				break
			default:
				break
		}

		return parsedDate
	} catch (error) {
		log(error)
		return 'Invalid Date'
	}
}

export const getWeekSlotsTillMonthEnd = (date: Date, firstDayOfWeek?: number): number => {
	const currentDay = date.getDate()
	const firstWeekDay = getAdjustedDay(date)
	const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0)
	const lastWeekDay = getAdjustedDay(lastDay)
	const remainingDays = lastDay.getDate() - currentDay + 1
	const totalWeekSlots = remainingDays + firstWeekDay + (6 - lastWeekDay)
	return Math.ceil(totalWeekSlots / 7)
}

export const getMonthDifference = (
	date1: string | number | Date | undefined,
	date2: string | number | Date | undefined
): number => {
	if (date1 === undefined || date2 === undefined) return 0
	const firstDate = new Date(date1)
	const secondDate = new Date(date2)
	if (isNaN(firstDate.getTime()) || isNaN(secondDate.getTime()) || date1 === 0 || date2 === 0) return 0
	const year1 = firstDate.getFullYear()
	const month1 = firstDate.getMonth()
	const year2 = secondDate.getFullYear()
	const month2 = secondDate.getMonth()
	const yearDifference = year2 - year1
	const monthDifference = month2 - month1
	return yearDifference * 12 + monthDifference
}

export const getAdjustedDay = (date: Date, firstDayOfWeek?: number | undefined): number =>
	(date.getDay() - (firstDayOfWeek ?? 1) + 7) % 7


export const computeRect = (rect: Rect, point: TableObject, stage?: Stage) => {
	let oy = point.style === 'v' ? point.pax * 50 : 50
	let ox = point.style === 'h' ? point.pax * 50 : 50

	rect.lt.x = Math.min(rect.lt.x, point.x, stage?.x ?? point.x)
	rect.lt.y = Math.min(rect.lt.y, point.y, stage?.y ?? point.y)

	rect.lb.x = Math.min(rect.lb.x, point.x, stage?.x ?? point.x)
	rect.lb.y = Math.max(rect.lb.y, point.y + oy, stage ? stage.y + stage.height : point.y + oy)

	rect.rt.x = Math.max(rect.rt.x, point.x + ox, stage ? stage.x + stage.width : point.x + ox)
	rect.rt.y = Math.min(rect.rt.y, point.y, stage?.y ?? point.y)

	rect.rb.x = Math.max(rect.rb.x, point.x + ox, stage ? stage.x + stage.width : point.x + ox)
	rect.rb.y = Math.max(rect.rb.y, point.y + oy, stage ? stage.y + stage.height : point.y + oy)

	rect.width = rect.rt.x - rect.lt.x
	rect.height = rect.rb.y - rect.rt.y
}

export const modifiersDidMatch = (shortcut: Shortcut, event: KeyboardEvent) => {
	let matches = 0
	if (!!shortcut.modifiers?.altKey === event.altKey) matches += 1
	if (!!shortcut.modifiers?.ctrlKey === event.ctrlKey) matches += 1
	if (!!shortcut.modifiers?.shiftKey === event.shiftKey) matches += 1
	if (!!shortcut.modifiers?.metaKey === event.metaKey) matches += 1
	return matches === 4
}

export const processTableLayout = (tables: TableObject[], h: number, w: number, d = 0, k = 1, venue: SafeVenueData) => {
	log('PROCESS TABLE LAYOUT EXECUTED')
	let maxes: ScoreMaxes = {}
	let min = 10000,
		max = -10000
	let rects = {
		cv: {
			lt: { x: min, y: min },
			rt: { x: max, y: min },
			lb: { x: min, y: max },
			rb: { x: max, y: max },
			height: 0,
			width: 0,
		},
		zv: {
			lt: { x: min, y: min },
			rt: { x: max, y: min },
			lb: { x: min, y: max },
			rb: { x: max, y: max },
			height: 0,
			width: 0,
		},
		za: {
			lt: { x: min, y: min },
			rt: { x: max, y: min },
			lb: { x: min, y: max },
			rb: { x: max, y: max },
			height: 0,
			width: 0,
		},
		za2: {
			lt: { x: min, y: min },
			rt: { x: max, y: min },
			lb: { x: min, y: max },
			rb: { x: max, y: max },
			height: 0,
			width: 0,
		},
		zb: {
			lt: { x: min, y: min },
			rt: { x: max, y: min },
			lb: { x: min, y: max },
			rb: { x: max, y: max },
			height: 0,
			width: 0,
		},
		zc: {
			lt: { x: min, y: min },
			rt: { x: max, y: min },
			lb: { x: min, y: max },
			rb: { x: max, y: max },
			height: 0,
			width: 0,
		},
	}

	let processedTables = tables.map((table) => {

		//calc table score maxes, col, side, row

		table.x += (TABLES_OVERRIDES as any)[venue.id]?.[table.id]?.x ?? 0
		table.y += (TABLES_OVERRIDES as any)[venue.id]?.[table.id]?.y ?? 0

		const {side, col, row} = getTableVars(table.name)
		maxes[side] = maxes[side] ?? {col: {}, row: {max: 0, min: 100}}
		maxes[side]!.col[row] = maxes[side]!.col[row] ?? {max: 0, min: 100}
		maxes[side]!.col[row].max = Math.max(maxes[side]!.col[row].max, col)
		maxes[side]!.row.max = Math.max(maxes[side]!.row.max, row)
		maxes[side]!.col[row].min = Math.min(maxes[side]!.col[row].min, col)
		maxes[side]!.row.min = Math.min(maxes[side]!.row.min, row)

		computeRect(rects.cv, table, venue.stage)
		computeRect(rects[table.zone], table)

		return {...table, col, side, row}

	}).map(table => ({...table, score: computeTableScore(table, maxes)}))

	return { appliedRotation: d, rects, processedTables }
}

export const applyVenueStyles = (venueData: SafeVenueData) => {


	if (venueData.id === 2) {
		// overwrite venue 2 colors till accepted in rumba
		document.documentElement.style.setProperty('--brand-zone-vip', "#E94464")
		document.documentElement.style.setProperty('--brand-zone-a', "#00a873")
		document.documentElement.style.setProperty('--brand-zone-b', "#447EE9")
		document.documentElement.style.setProperty('--brand-zone-c', "#DEA215")
		document.documentElement.style.setProperty('--brand', "#E94464")
		console.log(venueData.id)
		
	} else {
		document.documentElement.style.setProperty('--brand-zone-vip', venueData.colors.zv)
		document.documentElement.style.setProperty('--brand-zone-a', venueData.colors.za)
		document.documentElement.style.setProperty('--brand-zone-b', venueData.colors.zb)
		document.documentElement.style.setProperty('--brand-zone-c', venueData.colors.zc)
		document.documentElement.style.setProperty('--brand', venueData.colors.brand)
	}

}

/**
 * returns how many adults seats can be replaced with child seats
 * in compliance with the current rule
 */
export const calcMaxChildren = (adults: number) => {
	let a = adults * CHILDREN_PER_ADULT,
		b = 0

	while (true) {
		a -= CHILDREN_PER_ADULT + 1
		if (a < 0) break
		b += 1
	}

	return b
}

export const getTableVars = (str: string) => {
	let side: 'right' | 'front' | 'left', row: number, col: number

	switch (booking.venueId) {
		case 1:
			let row_str = str.substring(0, str.length - 1)
			let col_str = str.substring(str.length - 1)

			if (isNaN(Number(row_str))) {
				side = 'right'
				row = row_str.charCodeAt(0) - 64
			} else {
				side = 'front'
				row = Number(row_str)
			}

			col = Number(col_str)

			break
		case 2:
			let [str0, str1, str2] = str.split('')
			side = str0 === '3' ? 'left' : str0 === '2' ? 'front' : 'right'
			row = Number(str1)
			col = Number(str2)

			break
		case 3:
			side = 'front'
			row = Number(str.substring(0, str.length - 1))
			col = Number(str.substring(str.length - 1))
			break
	}

	return { side, row, col }
}

export const computeTableScore = (table: TableObject, maxes: ScoreMaxes): number => {
	// Valores por defecto
	let cmax = 0, rmax = 0, cmin = 0, rmin = 0, rmult = 1;
  
	// Configuración según el lado
	const sideConfig = maxes[table.side];
	if (sideConfig) {
	  cmax = sideConfig.col[table.row].max ?? 0;
	  cmin = sideConfig.col[table.row].min ?? 0;
	  rmax = sideConfig.row.max ?? 0;
	  rmin = sideConfig.row.min ?? 0;
  
	  // Ajustar rmult según el lado
	  rmult = table.side === 'front' ? 2 : 1.5;
	}
  
	// Calcular puntuaciones de fila y columna
	const rowScore = (rmax + rmin) - table.row * rmult;
	const colScore = cmax - Math.abs((table.col) - (cmax + cmin) / 2);
  
	// Retornar la suma de puntuaciones
	return rowScore + colScore;
  };
  
  export const getTimeString = (date: string | number | Date) => {
	const dateObject = new Date(date)
	return `${dateObject.getHours().toString().padStart(2, '0')}:${dateObject.getMinutes().toString().padStart(2, '0')}`
}

export const parseProductPrice = (product: Product) => {
	return `${parseNum('EUR', product.price)}${PRODUCTS_WICH_QTY_IS_FIXED_TO_PAX.includes(product.id) ? `/${_('0ge_ticke__')}` : ''}`
}

export const getSortedTickets = (products: Product[], zone?: SafeZone) => {
	return products.filter(product => {
	
			if (product.type !== 'ticket' || !product.zone) return false
			if (product.name === 'Entrada Residente') return false
			if (zone && product.zone !== zone) return false
			
			return true
		}).sort((a, b) => {
			if (a.zone === b.zone && a.category === "adult") return -1
			if (a.zone === b.zone && b.category === "adult") return 1
			return ZONE_ORDERS[a.zone as SafeZone] - ZONE_ORDERS[b.zone as SafeZone]
		})
}

export const getAllProducts = (products: Product[]) => {
	const cancellationFee = products.find(product => product.category === "insurance" && product.name !== SURCHARGE_NAME)
	const all = getFnbProducts(products)
	if (cancellationFee) all.push(cancellationFee)
	return all
}
export const getFnbProducts = (products: Product[]) => {
	const excluded = ["BEBIDA EXTRA"]
	return products
		.filter(product => product.type === "fnb" && product.category === "dinner" && !excluded.includes(product.name))
		.sort((a, b) => b.price - a.price)
}


export const getEventOccupation = (tables: TableObject[], shifts: Shift[], zone?: SafeZone) => {

	let max = 0
	let occupied = 0

	for (let table of tables) {

		if (zone && table.zone !== zone) continue

		max += table.pax
		if (!!table.occupied) occupied += table.pax
	}

	const shift = shifts.find(shift => shift.id === booking.shiftId) ?? shifts[0]
	if (max === 0 || (shift && ((zone && !shift[`${zone}Open` as keyof typeof shift]) || !shift.available))) 
		//if zone or event closed or tables are not loaded yet, return an event 100% occupied
		return {max: 0, occupied: 0, available: 0,percentAvailable: 0, percentOccupied: 1}

	//normal response
	return {
		max, 
		occupied, 
		available: max - occupied,
		percentAvailable: 1 - occupied / max, 
		percentOccupied: occupied / max
	}
}

export const clearCmmCookie = () => {
	cookies.deleteCookie("cmm_journey")
}